import { CSSProperties, useEffect, useState } from 'react';
import useMessageYourFans from 'Hooks/useMessageYourFans';
import { ScheduleOptions } from 'modules/Types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TimezoneSelect, { ITimezoneOption } from 'components/shareable/TimezoneSelect';
import { clsx, styx } from 'modules/Utils';
import CardSymphony from 'elements/CardSymphony';
import OptionsSymphony from 'elements/OptionsSymphony';
import InputSymphony from 'elements/InputSymphony';
import dayjs from 'dayjs';

const { ASAP, SCHEDULED } = ScheduleOptions;

interface SchedulingTabProps {
    className?: string;
    style?: CSSProperties;
}

const SchedulingTab = ({
    className,
    style
}: SchedulingTabProps) => {
    const {
        details,
        setDetails,
    } = useMessageYourFans();

    const [selectedDate, setSelectedDate] = useState<Date | null>(
        details.date ? details.date.toDate() : null
    );
    const [selectedTimezone, setSelectedTimezone] = useState<ITimezoneOption>({
        value: 'America/Los_Angeles',
        title: 'America / Los Angeles [PDT]',
        abbrev: 'PDT'
    });

    const handleScheduleChange = (newSchedule: ScheduleOptions) => {
        setDetails({ ...details, scheduled: newSchedule });
    };

    const handleChangeDate = (date: Date | null) => {
        setSelectedDate(date);
        setDetails({
            ...details,
            date: date ? dayjs(date) : null
        });
    };

    const handleChangeTimezone = (timezone: ITimezoneOption) => {
        setSelectedTimezone(timezone);
        setDetails({
            ...details,
            timezone: { ...timezone }
        });
    };

    return (
        <div
            className={clsx('scheduleTabComponent', className)}
            style={styx(sty.scheduleTabComponent, style)}
        >
            <CardSymphony
                className="scheduleTabHeader"
                title="Schedule"
                subtitle={`Choose when to send your campaign. Select "ASAP" or "Scheduled" (coming soon!)`}
                size="small"
                isNoPadding
            />

            <OptionsSymphony
                className="scheduleOptions"value={details.scheduled}
                handleOptionSelected={(option) => handleScheduleChange(option as ScheduleOptions)}
                options={[
                    {
                        id: ASAP,
                        label: 'ASAP',
                        description: 'Your email will be sent as soon as you submit this campaign.',
                    },
                    {
                        id: SCHEDULED,
                        label: 'Scheduled',
                        description: 'Your email will be sent at the scheduled time.',
                        unavailable: true,
                        children: (
                            <div
                                className="scheduleOptionContainer"
                                style={sty.scheduleOptionContainer}
                            >
                                {details.scheduled === SCHEDULED &&
                                    <div
                                        className="scheduleSchedulingOptions"
                                        style={sty.scheduleSchedulingOptions}
                                        onClick={(e) => e.stopPropagation()} // prevent bubbling up to handleSchedule
                                    >
                                        {/* NOTE: the datepicker simply needs to be wrapped in an empty div */}
                                        <div className="scheduleDateInputContainer">
                                            <DatePicker
                                                showTimeSelect
                                                placeholderText="Set send time"
                                                dateFormat="MMMM d, yyyy h:mm aa"
                                                minDate={dayjs().toDate()}
                                                selected={selectedDate}
                                                onChange={(date) => handleChangeDate(date)}
                                                customInput={
                                                    <InputSymphony
                                                        className="scheduleDateInput"
                                                        style={sty.scheduleDateInput}
                                                        value={selectedDate ? dayjs(selectedDate).format('MMMM D, YYYY h:mm A') : ''}
                                                        onChange={(value) => handleChangeDate(dayjs(value).toDate())}
                                                    />
                                                }
                                            />
                                        </div>
                                        <div
                                            className="scheduleTimezoneSelect"
                                            style={sty.timezoneSelect}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <TimezoneSelect
                                                labelStyle="abbrevNoPrefix"
                                                timezone={selectedTimezone}
                                                onTimezoneSelect={(timezone) => handleChangeTimezone(timezone)}
                                            />
                                        </div>
                                    </div>
                                }
                            </div>
                        )
                    }
                ]}
            />
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    scheduleTabComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 24,
        background: 'white',
        borderRadius: 8,
    },
    scheduleOptionContainer: {
        width: '100%',
    },
    scheduleSchedulingOptions: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        width: '100%',
        justifyContent: 'flex-end',
    },
    scheduleDateInput: {
        minWidth: 280,
    },
    timezoneSelect: {
        width: 'fit-content',
    },
};

export default SchedulingTab; 